// Esquema declarativo de campos por tipo de nó (telas simples/médias).
// Nós complexos (condition, switch, moveToStage, start, mídia, WA, agendamento) têm
// componente dedicado no registry. Campos seguem a spec do projeto original.
// showIf: { key, value } — mostra campo só quando data[key] === value.
// optionsFrom: 'agents'|'teams'|'labels'|'slaPolicies' (injetado via aux).
// arrayWrap: grava/lê em data[arrayWrap][0] em vez de data[key].
//
// i18n: label/placeholder/hint/note e os label das options ESTÁTICAS são CHAVES i18n
// (namespace CHATBOT.schema.*); o SchemaConfig.vue chama t() sobre elas ao renderizar.
// Options dinâmicas (optionsFrom) NÃO são traduzidas (vêm de dados da conta).

const STATUS = [
  { value: 'open', label: 'CHATBOT.schema.statusOptions.open' },
  { value: 'pending', label: 'CHATBOT.schema.statusOptions.pending' },
  { value: 'resolved', label: 'CHATBOT.schema.statusOptions.resolved' },
];

export const SCHEMAS = {
  sendText: {
    fields: [
      {
        key: 'message',
        type: 'textarea',
        label: 'CHATBOT.schema.sendText.message',
        rows: 5,
        picker: true,
        placeholder: 'Olá {{contato}}, obrigado por entrar em contato!',
        hint: 'CHATBOT.schema.sendText.hint',
      },
    ],
  },

  changeStatus: {
    fields: [
      {
        key: 'status',
        type: 'select',
        label: 'CHATBOT.schema.changeStatus.status',
        options: [
          { value: '', label: 'CHATBOT.schema.selectPlaceholder' },
          ...STATUS,
        ],
      },
      {
        key: 'toolDescription',
        type: 'textarea',
        label: 'CHATBOT.schema.toolDescription',
        rows: 2,
        placeholder: 'CHATBOT.schema.changeStatus.toolDescPlaceholder',
      },
    ],
  },

  delay: {
    fields: [
      {
        key: 'delayType',
        type: 'buttongroup',
        label: 'CHATBOT.schema.delay.type',
        default: 'fixed',
        options: [
          { value: 'fixed', label: 'CHATBOT.schema.delay.fixed' },
          { value: 'range', label: 'CHATBOT.schema.delay.range' },
        ],
      },
      {
        key: 'seconds',
        type: 'number',
        label: 'CHATBOT.schema.delay.duration',
        min: 1,
        max: 60,
        placeholder: '5',
        showIf: { key: 'delayType', value: 'fixed' },
      },
      {
        key: 'minSeconds',
        type: 'number',
        label: 'CHATBOT.schema.delay.min',
        min: 1,
        max: 59,
        placeholder: '5',
        showIf: { key: 'delayType', value: 'range' },
      },
      {
        key: 'maxSeconds',
        type: 'number',
        label: 'CHATBOT.schema.delay.max',
        min: 2,
        max: 60,
        placeholder: '10',
        showIf: { key: 'delayType', value: 'range' },
      },
    ],
  },

  sequenceDelay: {
    fields: [
      {
        key: 'value',
        type: 'number',
        label: 'CHATBOT.schema.sequenceDelay.value',
        min: 1,
        default: 1,
        placeholder: 'CHATBOT.schema.sequenceDelay.quantityPlaceholder',
      },
      {
        key: 'unit',
        type: 'select',
        label: 'CHATBOT.schema.sequenceDelay.unit',
        default: 'days',
        options: [
          { value: 'days', label: 'CHATBOT.schema.sequenceDelay.units.days' },
          { value: 'hours', label: 'CHATBOT.schema.sequenceDelay.units.hours' },
          {
            value: 'minutes',
            label: 'CHATBOT.schema.sequenceDelay.units.minutes',
          },
          {
            value: 'seconds',
            label: 'CHATBOT.schema.sequenceDelay.units.seconds',
          },
        ],
      },
    ],
    note: 'CHATBOT.schema.sequenceDelay.note',
  },

  applySLA: {
    fields: [
      {
        key: 'slaId',
        type: 'select',
        label: 'CHATBOT.schema.applySLA.policy',
        optionsFrom: 'slaPolicies',
        placeholder: 'CHATBOT.schema.applySLA.placeholder',
      },
    ],
  },

  waitForResponse: {
    fields: [
      {
        key: 'timeout',
        type: 'number',
        label: 'CHATBOT.schema.waitForResponse.timeout',
        min: 0,
        placeholder: 'CHATBOT.schema.waitForResponse.timeoutPlaceholder',
        hint: 'CHATBOT.schema.waitForResponse.timeoutHint',
      },
    ],
    note: 'CHATBOT.schema.waitForResponse.note',
  },

  input: {
    fields: [
      {
        key: 'variableName',
        type: 'text',
        label: 'CHATBOT.schema.input.variableName',
        default: 'userInput',
        placeholder: 'nome_da_variavel',
        hint: 'CHATBOT.schema.input.variableHint',
      },
      {
        key: 'message',
        type: 'textarea',
        label: 'CHATBOT.schema.input.message',
        rows: 3,
        picker: true,
        placeholder: 'CHATBOT.schema.input.messagePlaceholder',
      },
    ],
  },

  setVariable: {
    fields: [
      {
        key: 'variableName',
        type: 'text',
        label: 'CHATBOT.schema.setVariable.variableName',
        placeholder: 'nomeDaVariavel',
      },
      {
        key: 'mode',
        type: 'select',
        label: 'CHATBOT.schema.setVariable.mode',
        default: 'custom',
        options: [
          { value: 'custom', label: 'CHATBOT.schema.setVariable.modes.custom' },
          {
            value: 'javascript',
            label: 'CHATBOT.schema.setVariable.modes.javascript',
          },
          { value: 'empty', label: 'CHATBOT.schema.setVariable.modes.empty' },
          { value: 'now', label: 'CHATBOT.schema.setVariable.modes.now' },
          {
            value: 'randomId',
            label: 'CHATBOT.schema.setVariable.modes.randomId',
          },
          {
            value: 'momentOfDay',
            label: 'CHATBOT.schema.setVariable.modes.momentOfDay',
          },
          {
            value: 'randomNumber',
            label: 'CHATBOT.schema.setVariable.modes.randomNumber',
          },
        ],
      },
      {
        key: 'value',
        type: 'textarea',
        rows: 2,
        label: 'CHATBOT.schema.setVariable.value',
        picker: true,
        placeholder: 'Olá {{nomeUsuario}}!',
        showIf: { key: 'mode', value: 'custom' },
      },
      {
        key: 'code',
        type: 'textarea',
        rows: 3,
        label: 'CHATBOT.schema.setVariable.code',
        placeholder:
          '// retorne o valor\nreturn "{{nomeUsuario}}".toUpperCase();',
        showIf: { key: 'mode', value: 'javascript' },
      },
      {
        key: 'dateFormat',
        type: 'select',
        label: 'CHATBOT.schema.setVariable.dateFormat',
        showIf: { key: 'mode', value: 'now' },
        options: [
          { value: 'iso', label: 'ISO 8601' },
          { value: 'dd/mm/yyyy', label: 'DD/MM/YYYY' },
          { value: 'dd/mm/yyyy hh:mm', label: 'DD/MM/YYYY HH:MM' },
          {
            value: 'timestamp',
            label: 'CHATBOT.schema.setVariable.timestampMs',
          },
        ],
      },
      {
        key: 'min',
        type: 'number',
        label: 'CHATBOT.schema.setVariable.min',
        default: 1,
        showIf: { key: 'mode', value: 'randomNumber' },
      },
      {
        key: 'max',
        type: 'number',
        label: 'CHATBOT.schema.setVariable.max',
        default: 100,
        showIf: { key: 'mode', value: 'randomNumber' },
      },
    ],
  },

  customAttribute: {
    fields: [
      {
        key: 'attributeType',
        type: 'buttongroup',
        label: 'CHATBOT.schema.customAttribute.attributeType',
        default: 'conversation',
        options: [
          {
            value: 'conversation',
            label: 'CHATBOT.schema.customAttribute.conversation',
          },
          { value: 'contact', label: 'CHATBOT.schema.customAttribute.contact' },
        ],
      },
      {
        key: 'attributeKey',
        type: 'text',
        label: 'CHATBOT.schema.customAttribute.attributeKey',
        placeholder: 'CHATBOT.schema.customAttribute.attributeKeyPlaceholder',
      },
      {
        key: 'action',
        type: 'buttongroup',
        label: 'CHATBOT.schema.customAttribute.action',
        default: 'set',
        options: [
          { value: 'set', label: 'CHATBOT.schema.customAttribute.actionSet' },
          { value: 'get', label: 'CHATBOT.schema.customAttribute.actionGet' },
          {
            value: 'delete',
            label: 'CHATBOT.schema.customAttribute.actionDelete',
          },
        ],
      },
      {
        key: 'value',
        type: 'text',
        label: 'CHATBOT.schema.customAttribute.value',
        picker: true,
        placeholder: 'CHATBOT.schema.customAttribute.valuePlaceholder',
        showIf: { key: 'action', value: 'set' },
      },
      {
        key: 'saveValueTo',
        type: 'text',
        label: 'CHATBOT.schema.customAttribute.saveValueTo',
        placeholder: 'nomeDaVariavel',
        showIf: { key: 'action', value: 'get' },
      },
    ],
  },

  labels: {
    fields: [
      {
        key: 'action',
        type: 'select',
        label: 'CHATBOT.schema.labels.action',
        default: 'add',
        options: [
          { value: 'add', label: 'CHATBOT.schema.labels.add' },
          { value: 'replace', label: 'CHATBOT.schema.labels.replace' },
          { value: 'remove', label: 'CHATBOT.schema.labels.remove' },
        ],
      },
      {
        key: 'label',
        type: 'select',
        label: 'CHATBOT.schema.labels.label',
        optionsFrom: 'labels',
        placeholder: 'CHATBOT.schema.selectPlaceholder',
        arrayWrap: 'labels',
      },
    ],
  },

  assign: {
    fields: [
      {
        key: 'assignType',
        type: 'select',
        label: 'CHATBOT.schema.assign.assignType',
        default: 'agent',
        options: [
          { value: 'agent', label: 'CHATBOT.schema.assign.agent' },
          { value: 'team', label: 'CHATBOT.schema.assign.team' },
        ],
      },
      {
        key: 'assignId',
        type: 'select',
        label: 'CHATBOT.schema.assign.agent',
        optionsFrom: 'agents',
        placeholder: 'CHATBOT.schema.assign.selectAgent',
        showIf: { key: 'assignType', value: 'agent' },
      },
      {
        key: 'assignId',
        type: 'select',
        label: 'CHATBOT.schema.assign.team',
        optionsFrom: 'teams',
        placeholder: 'CHATBOT.schema.assign.selectTeam',
        showIf: { key: 'assignType', value: 'team' },
      },
      {
        key: 'toolDescription',
        type: 'textarea',
        label: 'CHATBOT.schema.toolDescription',
        rows: 2,
        placeholder: 'CHATBOT.schema.assign.toolDescPlaceholder',
      },
    ],
  },

  aiAgent: {
    fields: [
      {
        key: 'provider',
        type: 'select',
        label: 'CHATBOT.schema.aiAgent.provider',
        placeholder: 'CHATBOT.schema.aiAgent.selectProvider',
        options: [
          { value: 'openai_oauth', label: '🧠 ChatGPT (sua conta)' },
          { value: 'openai', label: '🤖 OpenAI' },
          { value: 'groq', label: '⚡ Groq' },
          { value: 'openrouter', label: '🔀 OpenRouter' },
          { value: 'google', label: '✨ Google Gemini' },
        ],
      },
      {
        key: 'model',
        type: 'text',
        label: 'CHATBOT.schema.aiAgent.model',
        placeholder: 'ex: gpt-4o-mini',
        showIf: { key: 'provider', truthy: true },
      },
      {
        key: 'prompt',
        type: 'textarea',
        label: 'CHATBOT.schema.aiAgent.prompt',
        rows: 5,
        picker: true,
        placeholder: 'CHATBOT.schema.aiAgent.promptPlaceholder',
      },
      {
        key: 'temperature',
        type: 'range',
        label: 'CHATBOT.schema.aiAgent.temperature',
        min: 0,
        max: 1,
        step: 0.1,
        default: 0.7,
      },
      {
        key: 'maxTokens',
        type: 'number',
        label: 'CHATBOT.schema.aiAgent.maxTokens',
        min: 100,
        max: 4000,
        step: 100,
        default: 1000,
      },
      {
        key: 'sendToChat',
        type: 'checkbox',
        label: 'CHATBOT.schema.aiAgent.sendToChat',
        default: true,
      },
      {
        key: 'transcribeAudio',
        type: 'checkbox',
        label: 'CHATBOT.schema.aiAgent.transcribeAudio',
      },
      {
        key: 'readImages',
        type: 'checkbox',
        label: 'CHATBOT.schema.aiAgent.readImages',
      },
      {
        key: 'useMemory',
        type: 'checkbox',
        label: 'CHATBOT.schema.aiAgent.useMemory',
        hint: 'CHATBOT.schema.aiAgent.useMemoryHint',
        default: true,
      },
      {
        key: 'memorySize',
        type: 'number',
        label: 'CHATBOT.schema.aiAgent.memorySize',
        min: 1,
        max: 30,
        step: 1,
        default: 10,
        showIf: { key: 'useMemory', truthy: true },
      },
      {
        key: 'fetchConversationData',
        type: 'checkbox',
        label: 'CHATBOT.schema.aiAgent.fetchConversationData',
        hint: 'CHATBOT.schema.aiAgent.fetchConversationDataHint',
      },
      {
        key: 'knowledgeBaseId',
        type: 'select',
        label: 'CHATBOT.schema.aiAgent.knowledgeBase',
        optionsFrom: 'knowledgeBases',
        hint: 'CHATBOT.schema.aiAgent.knowledgeBaseHint',
      },
      {
        key: 'saveResponseTo',
        type: 'text',
        label: 'CHATBOT.schema.aiAgent.saveResponseTo',
        placeholder: 'nome_da_variavel',
      },
    ],
    note: 'CHATBOT.schema.aiAgent.note',
  },

  generateImage: {
    fields: [
      {
        key: 'provider',
        type: 'select',
        label: 'CHATBOT.schema.generateImage.provider',
        placeholder: 'CHATBOT.schema.selectPlaceholder',
        options: [
          {
            value: 'pollinations',
            label: 'CHATBOT.schema.generateImage.pollinations',
          },
          { value: 'openai', label: 'OpenAI DALL·E' },
          { value: 'stability', label: 'Stability AI' },
          { value: 'google', label: 'Google Imagen' },
        ],
      },
      {
        key: 'model',
        type: 'text',
        label: 'CHATBOT.schema.generateImage.model',
        placeholder: 'ex: dall-e-3 / flux',
        showIf: { key: 'provider', truthy: true },
      },
      {
        key: 'prompt',
        type: 'textarea',
        label: 'CHATBOT.schema.generateImage.prompt',
        rows: 6,
        picker: true,
        placeholder: 'CHATBOT.schema.generateImage.promptPlaceholder',
      },
      {
        key: 'size',
        type: 'select',
        label: 'CHATBOT.schema.generateImage.size',
        showIf: { key: 'provider', value: 'openai' },
        options: [
          { value: '1024x1024', label: '1024x1024' },
          { value: '1792x1024', label: '1792x1024' },
          { value: '1024x1792', label: '1024x1792' },
        ],
      },
      {
        key: 'quality',
        type: 'select',
        label: 'CHATBOT.schema.generateImage.quality',
        showIf: { key: 'provider', value: 'openai' },
        options: [
          { value: 'standard', label: 'Standard' },
          { value: 'hd', label: 'HD' },
        ],
      },
      {
        key: 'style',
        type: 'select',
        label: 'CHATBOT.schema.generateImage.style',
        showIf: { key: 'provider', value: 'openai' },
        options: [
          { value: 'vivid', label: 'CHATBOT.schema.generateImage.styleVivid' },
          {
            value: 'natural',
            label: 'CHATBOT.schema.generateImage.styleNatural',
          },
        ],
      },
      {
        key: 'sendToChat',
        type: 'checkbox',
        label: 'CHATBOT.schema.generateImage.sendToChat',
        default: true,
      },
      {
        key: 'saveImageUrl',
        type: 'text',
        label: 'CHATBOT.schema.generateImage.saveImageUrl',
        placeholder: 'nome_da_variavel',
      },
      {
        key: 'toolDescription',
        type: 'textarea',
        label: 'CHATBOT.schema.toolDescription',
        rows: 2,
        placeholder: 'CHATBOT.schema.toolDescPlaceholder',
      },
    ],
    note: 'CHATBOT.schema.generateImage.note',
  },

  cancelAppointment: {
    fields: [
      {
        key: 'appointmentIdMode',
        type: 'buttongroup',
        label: 'CHATBOT.schema.cancelAppointment.idMode',
        default: 'variable',
        options: [
          {
            value: 'variable',
            label: 'CHATBOT.schema.cancelAppointment.variable',
          },
          { value: 'fixed', label: 'CHATBOT.schema.cancelAppointment.fixed' },
        ],
      },
      {
        key: 'appointmentIdVariable',
        type: 'text',
        label: 'CHATBOT.schema.cancelAppointment.idVariable',
        placeholder: 'appointmentId',
        showIf: { key: 'appointmentIdMode', value: 'variable' },
      },
      {
        key: 'appointmentIdFixed',
        type: 'number',
        label: 'CHATBOT.schema.cancelAppointment.idFixed',
        placeholder: 'Ex: 42',
        showIf: { key: 'appointmentIdMode', value: 'fixed' },
      },
      {
        key: 'cancelReason',
        type: 'text',
        label: 'CHATBOT.schema.cancelAppointment.reason',
        placeholder: 'CHATBOT.schema.optional',
      },
    ],
    note: 'CHATBOT.schema.cancelAppointment.note',
  },

  scheduleAppointment: {
    fields: [
      {
        key: 'practitionerIdVar',
        type: 'text',
        label: 'CHATBOT.schema.scheduleAppointment.practitionerVar',
        placeholder: 'practitionerId',
      },
      {
        key: 'serviceIdVar',
        type: 'text',
        label: 'CHATBOT.schema.scheduleAppointment.serviceVar',
        placeholder: 'serviceId',
      },
      {
        key: 'dateVariable',
        type: 'text',
        label: 'CHATBOT.schema.scheduleAppointment.dateVar',
        placeholder: 'dataAgend',
      },
      {
        key: 'timeVariable',
        type: 'text',
        label: 'CHATBOT.schema.scheduleAppointment.timeVar',
        placeholder: 'horaAgend',
      },
      {
        key: 'notes',
        type: 'textarea',
        rows: 2,
        label: 'CHATBOT.schema.scheduleAppointment.notes',
        placeholder: 'CHATBOT.schema.scheduleAppointment.notesPlaceholder',
      },
      {
        key: 'saveResultTo',
        type: 'text',
        label: 'CHATBOT.schema.scheduleAppointment.saveResultTo',
        placeholder: 'appointmentId',
      },
      {
        key: 'sendConfirmation',
        type: 'checkbox',
        label: 'CHATBOT.schema.scheduleAppointment.sendConfirmation',
        default: true,
      },
      {
        key: 'confirmationTemplate',
        type: 'textarea',
        rows: 4,
        label: 'CHATBOT.schema.scheduleAppointment.confirmationTemplate',
        picker: true,
        showIf: { key: 'sendConfirmation', value: true },
        placeholder: '✅ Agendamento confirmado! {{data}} às {{hora}}',
      },
    ],
    note: 'CHATBOT.schema.scheduleAppointment.note',
  },

  checkAvailability: {
    fields: [
      {
        key: 'practitionerIdVar',
        type: 'text',
        label: 'CHATBOT.schema.checkAvailability.practitionerVar',
        placeholder: 'practitionerId',
      },
      {
        key: 'dateVariable',
        type: 'text',
        label: 'CHATBOT.schema.checkAvailability.dateVar',
        placeholder: 'dia',
      },
      {
        key: 'workStart',
        type: 'time',
        label: 'CHATBOT.schema.checkAvailability.workStart',
        default: '08:00',
      },
      {
        key: 'workEnd',
        type: 'time',
        label: 'CHATBOT.schema.checkAvailability.workEnd',
        default: '18:00',
      },
      {
        key: 'slotMinutes',
        type: 'number',
        label: 'CHATBOT.schema.checkAvailability.slotMinutes',
        min: 5,
        max: 480,
        step: 5,
        default: 60,
      },
      {
        key: 'maxSlots',
        type: 'number',
        label: 'CHATBOT.schema.checkAvailability.maxSlots',
        min: 1,
        max: 20,
        default: 8,
      },
      {
        key: 'outputFormat',
        type: 'textarea',
        rows: 3,
        label: 'CHATBOT.schema.checkAvailability.outputFormat',
        default: '🗓 Horários para {{data}}:\n\n{{horarios}}\n\nQual prefere?',
      },
      {
        key: 'saveResultTo',
        type: 'text',
        label: 'CHATBOT.schema.checkAvailability.saveResultTo',
        default: 'horariosDisponiveis',
      },
    ],
    note: 'CHATBOT.schema.checkAvailability.note',
  },

  aiSchedulingAgent: {
    fields: [
      {
        key: 'provider',
        type: 'select',
        label: 'CHATBOT.schema.aiSchedulingAgent.provider',
        default: 'openai',
        options: [
          { value: 'openai', label: 'OpenAI' },
          { value: 'groq', label: 'Groq' },
        ],
      },
      {
        key: 'model',
        type: 'text',
        label: 'CHATBOT.schema.aiSchedulingAgent.model',
        placeholder: 'gpt-4o-mini',
      },
      {
        key: 'prompt',
        type: 'textarea',
        rows: 6,
        label: 'CHATBOT.schema.aiSchedulingAgent.prompt',
        picker: true,
        placeholder: 'CHATBOT.schema.aiSchedulingAgent.promptPlaceholder',
      },
      {
        key: 'autoBook',
        type: 'checkbox',
        label: 'CHATBOT.schema.aiSchedulingAgent.autoBook',
        default: true,
      },
      {
        key: 'saveResultTo',
        type: 'text',
        label: 'CHATBOT.schema.aiSchedulingAgent.saveResultTo',
        placeholder: 'appointmentId',
      },
    ],
    note: 'CHATBOT.schema.aiSchedulingAgent.note',
  },

  knowledgeBase: {
    fields: [
      {
        key: 'name',
        type: 'text',
        label: 'CHATBOT.schema.knowledgeBase.name',
        placeholder: 'Catálogo 2026',
      },
    ],
    note: 'CHATBOT.schema.knowledgeBase.note',
  },

  // Nós informativos (sem campos)
  checkResponse: { fields: [], note: 'CHATBOT.schema.checkResponse.note' },
  end: { fields: [], note: 'CHATBOT.schema.end.note' },
  sequenceEnd: { fields: [], note: 'CHATBOT.schema.sequenceEnd.note' },
};

export const hasSchema = type =>
  Object.prototype.hasOwnProperty.call(SCHEMAS, type);
